/**
 * La postazione di ricezione.
 *
 * Porta dal prototipo le tre cose che ne fanno una postazione e non una pagina:
 *
 * - **si entra da qui**, senza scegliere un fornitore e senza attraversare una
 *   ricerca. Il primo gesto è il capo;
 * - **un gesto, un posto**: la zona sotto la foto contiene sempre e solo
 *   l'azione successiva, ad altezza fissa. Cambia il contenuto, non la
 *   posizione (regola 2-ter);
 * - **la colonna della taglia è il tasto**: un tocco è un capo trovato nella
 *   scatola. Correggere e dichiarare un fallato sono **modi a colpo singolo**,
 *   non pulsanti permanenti dentro la colonna (regola 2-quater).
 *
 * Nessun conto si fa qui: quanti capi mancano, quali taglie vanno etichettate e
 * se la cassetta è confermabile arrivano già decisi da `vistaBanco`, che è nel
 * dominio. La schermata li mette in pagina.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { EsitoComando, EsitoFoto } from '@errebi/shared';
import type { BenchView, SizeView } from '@errebi/domain';

import { creaBanco } from './api';
import { Notifiche } from '../../components/notifications';
import { Spazio, Testata, Voce } from '../../components/header';
import { chiavi, useSessione } from '@errebi/sessione';
import { OrdiniAttesi } from './expected-orders';
import { ArticleSearch } from './search-article';
import { Banco } from './bench';
import { CapoInCabina } from './booth';
import type { Modo } from './mode';
import { ChiusureInSospeso, RigheAttese } from './lines';
import { ZonaGesto } from './action-zone';
import { Camera } from '../../components/camera';
import { Modale } from '../../components/modal';
import { Avviso } from '../../components/notice';

/**
 * The overlay in front of the bench, if any.
 *
 * `bolle` is the delivery-note panel — the road for whoever starts from paper —
 * and `articoli` is the article search (rule 2-quinquies), the road for a
 * garment that carries no code to read.
 */
type Pannello = null | 'bolle' | 'articoli';

const POSTAZIONE = 'banco-1';

export function Ricezione() {
  const { identita, esegui } = useSessione();
  const coda = useQueryClient();
  const [ultimo, setUltimo] = useState<EsitoComando | null>(null);
  const [modo, setModo] = useState<Modo>(null);
  /**
   * How many garments the next size tap is worth. Empty is the usual tap, one
   * garment; like the mode, it lasts one tap and then clears.
   */
  const [quantita, setQuantita] = useState('');
  /**
   * Which overlay is open, if any. Three states and not two booleans: the two
   * panels cover the same spot and Escape has to close «whatever is in front»,
   * which is a question one variable can answer and two cannot agree on.
   */
  const [pannello, setPannello] = useState<Pannello>(null);
  // il dialogo dell'annullamento: aperto solo quando c'è davvero da perdere
  const [chiedeAnnullo, setChiedeAnnullo] = useState(false);
  /**
   * La cassetta che si sta per svuotare, o `null`.
   *
   * Il codice e non un booleano: il gesto arriva da una tessera fra tante, e il
   * dialogo deve poter dire **quale** — «Svuotare C31?» — o chiederebbe conferma
   * di qualcosa che chi legge deve indovinare.
   */
  const [chiedeSvuota, setChiedeSvuota] = useState<string | null>(null);
  /** The closing summary is open: nothing is sent until it is confirmed. */
  const [chiedeChiusura, setChiedeChiusura] = useState(false);
  /** Counts refused commands; the «Ricevuti» fields reset to the view on each. */
  const [rifiuti, setRifiuti] = useState(0);
  /**
   * La fotocamera aperta, e **perché** (regola 38-bis).
   *
   * `null` chiusa; `{taglia: null}` è lo scatto per l'archivio; `{taglia}` è la
   * foto di un capo difettoso, che dopo lo scatto fa partire il fallato. Un
   * oggetto e non due booleani: la seconda strada deve ricordare *quale* taglia
   * l'ha aperta, e due variabili che devono restare d'accordo sono due
   * variabili che un giorno non lo sono.
   */
  const [fotocamera, setFotocamera] = useState<{ taglia: string | null } | null>(null);

  const banco = creaBanco(POSTAZIONE, identita?.token ?? '');

  const vista = useQuery({
    queryKey: chiavi.banco(POSTAZIONE),
    // L'unica lettura che resta a cast, e con la sua ragione: `VistaBanco` è un
    // tipo di `@errebi/domain`, condiviso davvero fra i due lati (il dominio la
    // calcola, la schermata la disegna). Riscriverlo in Zod qui vorrebbe dire
    // mantenerne due, e la seconda copia sarebbe libera di divergere dalla
    // prima — cioè il difetto che questa fase serve a togliere, rimesso dentro
    // da un'altra porta.
    queryFn: () => banco.vista() as Promise<BenchView>,
    refetchOnWindowFocus: true,
  });

  /**
   * Le bolle si leggono solo quando il pannello si apre.
   *
   * Sono una `find` sugli acquisti di DecoNetwork, e la postazione passa la
   * giornata a scansionare capi: scaricarle all'ingresso vorrebbe dire pagarle
   * a ogni turno per l'eccezione di chi parte dalla carta — e mostrarle vecchie
   * di ore quando serve davvero.
   */
  const attesi = useQuery({
    queryKey: chiavi.attesi(POSTAZIONE),
    queryFn: () => banco.attesi(),
    enabled: pannello === 'bolle',
  });

  /*
   * L'esito è l'unione dei due, e non solo `EsitoComando`: lo scatto risponde
   * con un `EsitoFoto`, che è un esito di comando con l'id della foto in più.
   * Dichiararlo qui è ciò che permette a `mandaScatto` di leggere quell'id
   * senza un cast — e a tutti gli altri gesti di non accorgersi di niente,
   * perché `EsitoFoto` è un `EsitoComando` a tutti gli effetti.
   */
  const gesto = useMutation({
    mutationFn: (azione: (token: string) => Promise<EsitoComando | EsitoFoto>) => esegui(azione),
    onSuccess: (esito) => {
      setUltimo(esito);
      if (!esito.ok) setRifiuti((n) => n + 1);
      void coda.invalidateQueries({ queryKey: chiavi.banco(POSTAZIONE) });
    },
    onError: (e: Error) => {
      setUltimo({ ok: false, titolo: 'Errore', motivo: e.message, regola: '—' });
      setRifiuti((n) => n + 1);
    },
  });

  /** Ogni gesto passa da qui: la conferma d'identità la gestisce la sessione. */
  const fai = (azione: (b: ReturnType<typeof creaBanco>) => Promise<EsitoComando>) => () =>
    gesto.mutate((token) => azione(creaBanco(POSTAZIONE, token)));

  /**
   * La tastiera della postazione: `/` apre le bolle, `?` la ricerca articolo,
   * Esc chiude.
   *
   * Un solo gestore, perché due che ascoltano lo stesso tasto litigano: si
   * chiude sempre ciò che sta davanti, quindi Esc chiude prima il dialogo di
   * annullamento, poi il pannello, e solo quando non c'è più niente sopra
   * torna ad annullare il modo, che dura un colpo e in cui non ci si resta
   * dentro.
   *
   * `?` is `/` with a shift, and that is the whole reason it was picked: the two
   * roads that skip the scanner sit on the same key. It is also a character no
   * SKU can contain — they are `«codice»-«idcolore»` — so a reader firing into
   * the code field can never open a panel by accident.
   */
  useEffect(() => {
    const suTasto = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (chiedeChiusura) setChiedeChiusura(false);
        else if (chiedeAnnullo) setChiedeAnnullo(false);
        // La fotocamera sta davanti a tutto, quindi Esc chiude lei per prima.
        // Chiuderla è un gesto con una conseguenza — un fallato aperto parte
        // senza foto — quindi passa dalla stessa funzione del tasto.
        else if (fotocamera) chiudiFotocamera();
        else if (pannello) setPannello(null);
        else {
          setModo(null);
          setQuantita('');
        }
        return;
      }
      // senza preventDefault la barra finisce dentro il campo di ricerca che
      // sta per prendere il fuoco, e la prima lettera cercata è sempre sbagliata
      // Col dialogo di annullamento davanti «/» non deve aprire il pannello
      // dietro: si chiude sempre ciò che sta sopra, non quello che sta sotto.
      if ((e.key === '/' || e.key === '?') && !pannello && !chiedeAnnullo && !fotocamera) {
        e.preventDefault();
        // `?` senza capo agganciato non ha niente da aprire: la ricerca è già
        // sotto la foto, e `vista.data` è l'unico posto che sa se c'è un capo.
        const capo = (vista.data as BenchView | undefined)?.agganciata ?? null;
        if (e.key === '?' && capo === null) return;
        setPannello(e.key === '?' ? 'articoli' : 'bolle');
      }
    };
    window.addEventListener('keydown', suTasto);
    return () => window.removeEventListener('keydown', suTasto);
    // `chiudiFotocamera` legge `fotocamera` e chiama `fai`, che si ricompone a
    // ogni render: la dipendenza dichiarata è lo stato, non la funzione.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pannello, chiedeAnnullo, chiedeChiusura, fotocamera, vista.data]);

  if (vista.isLoading) return <p style={{ padding: 24 }}>Apro la postazione…</p>;
  if (vista.isError) {
    return (
      <p className="avviso avviso--no" style={{ margin: 24 }}>
        {vista.error.message}
      </p>
    );
  }

  const v = vista.data as BenchView;

  /** Il tocco sulla taglia: il modo armato decide cosa vuol dire. */
  function toccaTaglia(t: SizeView) {
    const corrente = modo;
    setModo(null); // si consuma al primo tocco
    if (corrente === 'meno') return fai((b) => b.togliCapo(t.taglia))();
    // Il fallato passa dalla fotocamera: la regola 8 dice «fotografa il capo»,
    // e fino a oggi quella foto era dichiarata e mai acquisita. Non blocca —
    // «continua senza foto» manda il fallato lo stesso, con una prova in meno.
    if (corrente === 'fallato') return setFotocamera({ taglia: t.taglia });
    const n = quantita === '' ? undefined : Number(quantita);
    setQuantita('');
    return fai((b) => b.contaCapo(t.taglia, n))();
  }

  /**
   * Lo scatto è arrivato: si manda, e se la fotocamera era stata aperta da un
   * fallato si manda anche quello, con l'id della foto appena archiviata.
   *
   * Due richieste e non una, ed è deliberato: il fallato è un gesto
   * **attribuito**, quindi può tornare 403 e la sessione lo ritenta dopo la
   * password. Ritentare un multipart da un megabyte sul Wi-Fi del reparto è un
   * affare peggiore che ritentare un JSON con dentro un uuid.
   */
  async function mandaScatto(immagine: Blob) {
    const taglia = fotocamera?.taglia ?? null;
    setFotocamera(null);

    let id: string | undefined;
    try {
      const esito = await gesto.mutateAsync((token) =>
        creaBanco(POSTAZIONE, token).scattaFoto(immagine, taglia ?? undefined),
      );
      if (esito.ok && 'foto' in esito) id = esito.foto.id;
    } catch {
      // L'avviso lo ha già messo `onError`. Il fallato parte lo stesso: la foto
      // si chiede e non si pretende, e un capo difettoso non dichiarato è merce
      // contata come buona.
    }

    if (taglia !== null) fai((b) => b.segnaFallato(taglia, 1, id))();
  }

  /** Chiusa senza scattare: il fallato parte comunque, senza foto. */
  function chiudiFotocamera() {
    const taglia = fotocamera?.taglia ?? null;
    setFotocamera(null);
    if (taglia !== null) fai((b) => b.segnaFallato(taglia, 1))();
  }

  /**
   * Aprire una bolla è un gesto come gli altri: passa da `fai`, quindi dalla
   * conferma d'identità se il permesso la chiede, e invalida la vista.
   *
   * Il pannello si chiude subito, e col modo armato se ne va anche quello: è un
   * altro arrivo, e la tastiera delle taglie a cui il modo si riferiva non c'è
   * più. Le cassette invece restano sul banco.
   */
  function apriArrivo(numero: string) {
    setPannello(null);
    setModo(null);
    fai((b) => b.apriArrivo(numero))();
  }

  /**
   * Hooking the line the operator picked from the article search (rule
   * 2-quinquies).
   *
   * It goes through the same `POST /capo` the arrival panel's rows use, and it
   * needs no size: the garment is hooked now, counted later. `hookGarment`
   * writes the line's purchase order into the state, so **the arrival follows
   * along** — the operator never has to open a delivery note.
   *
   * Same clearing as `apriArrivo`, for the same reason: the size keypad the
   * armed mode pointed at belongs to another line now.
   */
  function agganciaDaRicerca(ordine: string, riga: string) {
    setPannello(null);
    setModo(null);
    fai((b) => b.agganciaCapo(ordine, riga))();
  }

  /**
   * Staccare è un gesto come gli altri: passa da `fai`, quindi dalla conferma
   * d'identità se il permesso la chiede. Il modo armato se ne va con l'arrivo,
   * per la stessa ragione per cui se ne va aprendo un'altra bolla: la tastiera
   * delle taglie a cui si riferiva non c'è più.
   */
  function staccaArrivo() {
    setModo(null);
    fai((b) => b.staccaArrivo())();
  }

  /**
   * Chiede conferma solo se c'è da perdere: un «sei sicuro?» a vuoto insegna a
   * rispondere di sì senza leggere.
   *
   * Se `v.annullamento` manca del tutto — non dovrebbe darsi, l'invariante
   * dice che con un arrivo agganciato c'è sempre, ma uno skew di deploy (web
   * nuovo, API vecchia) può farlo mancare comunque — il default sicuro per un
   * gesto distruttivo è chiedere, non presumere che non ci sia niente da perdere.
   */
  function chiediAnnullo() {
    if (!v.annullamento || v.annullamento.perde) return setChiedeAnnullo(true);
    setModo(null);
    fai((b) => b.annullaArrivo())();
  }

  function annulla() {
    setChiedeAnnullo(false);
    setModo(null);
    fai((b) => b.annullaArrivo())();
  }

  return (
    <div className="shell">
      <Testata>
        <Voce k="Postazione" v={POSTAZIONE} />

        {/* quale bolla si sta ricevendo, o che non ce n'è nessuna */}
        <Voce k="Arrivo" v={v.arrivo ?? 'nessun arrivo agganciato'} />

        {v.arrivo && (
          <>
            <button className="mod" onClick={staccaArrivo} disabled={gesto.isPending}>
              Stacca
            </button>
            <button className="mod mod--no" onClick={chiediAnnullo} disabled={gesto.isPending}>
              Annulla arrivo
            </button>
            <button
              className="mod"
              onClick={() => setChiedeChiusura(true)}
              disabled={gesto.isPending}
            >
              Chiudi bolla
            </button>
          </>
        )}

        {v.agganciata && (
          <>
            <Voce
              k="Ordine"
              v={
                <>
                  {v.agganciata.aMagazzino
                    ? `a magazzino · bolla ${v.agganciata.acquisto}`
                    : `${v.agganciata.ordine} · ${v.agganciata.cliente}`}
                </>
              }
            />
            <Voce
              k="Articolo"
              v={
                <>
                  {v.agganciata.sku} {v.agganciata.colore}
                </>
              }
            />
          </>
        )}

        <Spazio />
        {/*
          «Cerca articolo» solo **a capo agganciato**: senza, la stessa ricerca
          è già nella zona gesto, e aprirne una seconda metterebbe due caselle
          identiche in pagina — l'operatrice dovrebbe scegliere in quale
          scrivere, che è una domanda che non deve nemmeno porsi.
        */}
        {v.agganciata && (
          <button
            className="mod"
            onClick={() => setPannello('articoli')}
            disabled={pannello !== null}
          >
            Cambia articolo <span className="mod__hint">?</span>
          </button>
        )}
        <button className="mod" onClick={() => setPannello('bolle')} disabled={pannello !== null}>
          Bolle attese <span className="mod__hint">/</span>
        </button>
        <Notifiche />
      </Testata>

      <div className="main">
        {/* ── la cabina luce ── */}
        <section className="booth">
          <div className="booth__stage">
            {v.agganciata ? (
              <CapoInCabina riga={v.agganciata} />
            ) : (
              <p className="booth__vuoto">
                Nessun capo agganciato. Scansiona un capo pescato dalla scatola: il sistema trova da
                sé l’ordine più vecchio che lo aspetta.
              </p>
            )}
          </div>

          <ZonaGesto
            v={v}
            modo={modo}
            setModo={setModo}
            quantita={quantita}
            setQuantita={setQuantita}
            fai={fai}
            toccaTaglia={toccaTaglia}
            onAggancia={agganciaDaRicerca}
            apriFotocamera={() => setFotocamera({ taglia: null })}
            inCorso={gesto.isPending}
          />

          <Banco v={v} fai={fai} chiediSvuota={setChiedeSvuota} />
        </section>

        {/* ── le righe attese ── */}
        <div>
          <RigheAttese v={v} fai={fai} inCorso={gesto.isPending} rifiuti={rifiuti} />
          <ChiusureInSospeso v={v} fai={fai} inCorso={gesto.isPending} />
        </div>
      </div>

      {/* la seconda strada: si parte dalla bolla di carta invece che dal capo */}
      {pannello === 'bolle' && (
        <Modale etichetta="Bolle in attesa" larga onChiudi={() => setPannello(null)}>
          {attesi.isPending ? (
            <p className="mod__hint">Leggo le bolle aperte…</p>
          ) : attesi.isError ? (
            <p className="avviso avviso--no" style={{ margin: 0 }}>
              {attesi.error.message}
            </p>
          ) : (
            <OrdiniAttesi schede={attesi.data} arrivo={v.arrivo} onApri={apriArrivo} />
          )}
          <div className="mods" style={{ marginTop: 12 }}>
            <button className="mod" onClick={() => setPannello(null)}>
              Chiudi <span className="mod__hint">Esc</span>
            </button>
          </div>
        </Modale>
      )}

      {/* la terza: il capo non ha un codice da leggere, e lo si cerca per nome
          (regola 2-quinquies). Niente da chiedere alla rete — `v.righe` è già la
          finestra intera, ordinata dal PO più vecchio. */}
      {pannello === 'articoli' && (
        <Modale etichetta="Cerca l’articolo" larga onChiudi={() => setPannello(null)}>
          <ArticleSearch
            righe={v.righe}
            arrivo={v.arrivo}
            intestazione={{
              titolo: 'Cambia articolo',
              nota: 'Scegli un’altra riga: il capo e l’arrivo si agganciano insieme.',
            }}
            onAggancia={agganciaDaRicerca}
          />
          <div className="mods" style={{ marginTop: 12 }}>
            <button className="mod" onClick={() => setPannello(null)}>
              Chiudi <span className="mod__hint">Esc</span>
            </button>
          </div>
        </Modale>
      )}

      {fotocamera !== null && (
        <Modale
          etichetta={
            fotocamera.taglia === null ? 'Fotografa la merce' : 'Fotografa il capo fallato'
          }
          onChiudi={chiudiFotocamera}
        >
          <Camera
            onScatto={(immagine) => void mandaScatto(immagine)}
            onChiudi={chiudiFotocamera}
            {...(v.agganciata === null
              ? {}
              : {
                  soggetto: `${v.agganciata.sku} · ${v.agganciata.colore}${
                    fotocamera.taglia === null ? '' : ` · taglia ${fotocamera.taglia}`
                  }`,
                })}
            inCorso={gesto.isPending}
          />
        </Modale>
      )}

      {chiedeAnnullo && v.annullamento && (
        <Modale
          etichetta={`Annullare l’arrivo ${v.arrivo}?`}
          ruolo="alertdialog"
          onChiudi={() => setChiedeAnnullo(false)}
        >
          <b className="gesto__k">Annullare l’arrivo {v.arrivo}?</b>
          <p className="mod__hint">
            {v.annullamento.cassette.join(', ')} torna
            {v.annullamento.cassette.length === 1 ? '' : 'no'} libera
            {v.annullamento.cassette.length === 1 ? '' : 'e'}. {v.annullamento.capi}{' '}
            {v.annullamento.capi === 1 ? 'capo torna' : 'capi tornano'} da ricevere.
            {/* con zero etichette non c'è niente da dire: «0 etichette sono
                annullate» è rumore, non un'informazione */}
            {v.annullamento.etichette > 0 && (
              <>
                {' '}
                {v.annullamento.etichette} etichett
                {v.annullamento.etichette === 1 ? 'a è annullata' : 'e sono annullate'}.
              </>
            )}
          </p>
          <div className="mods" style={{ marginTop: 12 }}>
            {/* il fuoco parte da qui: chi torna col dito già in movimento non
                deve trovarci sotto il tasto distruttivo */}
            <button className="mod" autoFocus onClick={() => setChiedeAnnullo(false)}>
              Non annullare <span className="mod__hint">Esc</span>
            </button>
            <button className="mod mod--no" onClick={annulla} disabled={gesto.isPending}>
              Sì, annulla
            </button>
          </div>
        </Modale>
      )}

      {chiedeChiusura && v.arrivo && (
        <Modale
          etichetta={`Chiudere la bolla ${v.arrivo}?`}
          ruolo="alertdialog"
          onChiudi={() => setChiedeChiusura(false)}
        >
          <b className="gesto__k">Chiudere la bolla {v.arrivo}?</b>
          {v.chiusura && v.chiusura.righeExpress > 0 && (
            <p>
              <b>Versamento sul Modula express</b> — {v.chiusura.righeExpress} righe,{' '}
              {v.chiusura.capiExpress} capi
            </p>
          )}
          {v.chiusura && v.chiusura.fuoriMissione.length > 0 && (
            <p className="avviso avviso--no" style={{ margin: '8px 0' }}>
              Non possono andare sul Modula express: {v.chiusura.fuoriMissione.join(', ')}.
            </p>
          )}
          <p className="mod__hint">
            Chiudere riceve tutta la bolla su DecoNetwork
            {v.chiusura ? `, anche i ${v.chiusura.capiClienti} capi dei clienti` : ''}. Si fa una
            volta sola.
          </p>
          <div className="mods" style={{ marginTop: 12 }}>
            <button className="mod" autoFocus onClick={() => setChiedeChiusura(false)}>
              Non chiudere <span className="mod__hint">Esc</span>
            </button>
            <button
              className="mod mod--si"
              disabled={gesto.isPending}
              onClick={() => {
                setChiedeChiusura(false);
                setModo(null);
                fai((b) => b.chiudiArrivo())();
              }}
            >
              {v.chiusura && v.chiusura.righeExpress > 0
                ? 'Chiudi bolla e manda al Modula express'
                : 'Chiudi bolla'}
            </button>
          </div>
        </Modale>
      )}

      {chiedeSvuota !== null && (
        <Modale
          etichetta={`Svuotare ${chiedeSvuota}?`}
          ruolo="alertdialog"
          onChiudi={() => setChiedeSvuota(null)}
        >
          <b className="gesto__k">Svuotare {chiedeSvuota}?</b>
          <p className="mod__hint">{cosaSiPerde(v, chiedeSvuota)}</p>
          <div className="mods" style={{ marginTop: 12 }}>
            {/* Il fuoco parte dall'uscita, come nel dialogo dell'annullamento:
                chi torna col dito già in movimento non deve trovarci sotto il
                tasto che disfa. */}
            <button className="mod" autoFocus onClick={() => setChiedeSvuota(null)}>
              Non svuotare <span className="mod__hint">Esc</span>
            </button>
            <button
              className="mod mod--no"
              disabled={gesto.isPending}
              onClick={() => {
                const codice = chiedeSvuota;
                setChiedeSvuota(null);
                fai((b) => b.svuota(codice))();
              }}
            >
              Sì, svuota
            </button>
          </div>
        </Modale>
      )}

      {/* Con `onChiudi` l'avviso galleggia, porta la X e gli esiti riusciti si
          tolgono da soli: al banco la pagina è piena, e una fascia che compare
          nel flusso sposterebbe la merce sotto le dita di chi conta. */}
      <Avviso esito={ultimo} onChiudi={() => setUltimo(null)} />
    </div>
  );
}

/**
 * Cosa si perde svuotando: si dice **prima**, con i numeri di adesso.
 *
 * Le etichette non si contano — la vista porta solo quelle *da* stampare — ma
 * dirlo con una frase basta: quel che conta è che chi legge sappia che la carta
 * già uscita va buttata, non quante siano.
 */
function cosaSiPerde(v: BenchView, codice: string): string {
  const c = v.cassetteBanco.find((x) => x.codice === codice);
  const capi = c?.capi ?? 0;
  return (
    `${codice} torna libera. ` +
    (capi === 0
      ? 'Non ci sono capi dentro.'
      : `${capi} ${capi === 1 ? 'capo torna' : 'capi tornano'} da ricevere, ` +
        'e le etichette già stampate sono da buttare.') +
    (c?.confermata === true ? ' Anche la conferma del contenuto cade.' : '')
  );
}
